/**
 * Contract "Reply to Submitter" MODAL — concept demo (Ken 2026-10-01).
 *
 * Opened from the memo in pane-3. Maria drafts a short note back to the submitter
 * (she doesn't have to address anything) and copies it into the Gmail thread. This
 * is NOT wired to Gmail — the draft is a starting point and "Paste to the thread in
 * Gmail" just copies it to the clipboard. Same overlay furniture as MarkupModal.
 */
import { useEffect, useState } from 'react'
import { Copy, Check, X } from 'lucide-react'

type Props = {
  submitterFirst?: string
  agreement?: string
  onClose: () => void
}

export default function ContractReplyModal({ submitterFirst, agreement, onClose }: Props) {
  const initial =
    `${submitterFirst || '[First Name]'},\n\n` +
    `Regarding ${agreement || '[describe the agreement submitted]'}, `
  const [text, setText] = useState(initial)
  const [copied, setCopied] = useState(false)

  // Esc closes; lock body scroll while open (mirrors MarkupModal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  const onPaste = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard denied — silent */ }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reply to submitter"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col rounded-lg border me-border shadow-2xl bg-[color:var(--me-surface)]"
        style={{ width: 'min(620px, 92vw)', maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b me-border shrink-0">
          <div className="me-text font-medium text-[13px]">Reply to Submitter</div>
          <button onClick={onClose} className="me-btn me-btn-outline" title="Close (Esc)" aria-label="Close">
            <X size={13} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="me-text-mute text-[11px] mb-2 leading-relaxed">
            Draft a note back to the submitter — you don't have to address anything.
            Refine the wording, then copy it into the Gmail thread.
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            autoFocus
            className="w-full resize-none rounded-md border me-border bg-[color:var(--me-panel)] me-text p-3 text-[13px] leading-relaxed outline-none"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t me-border shrink-0">
          <button onClick={onClose} className="me-btn me-btn-outline">Cancel</button>
          <button
            onClick={onPaste}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium text-white"
            style={{ background: 'var(--me-accent, #2563eb)' }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied — paste into Gmail' : 'Paste to the thread in Gmail'}
          </button>
        </div>
      </div>
    </div>
  )
}
